import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import styles from "./ImmunizationVaricellaViewNew.module.css";

import { AddButton, InputBox } from "@cpnw/ui";
import DashboardRadio from "components/DashboardRadio";
import FormDatePicker from "components/FormDatePicker";
import classNames from "classnames";
import { DATE_FORMAT_PATTERN } from "pages/enums";

const SubmissionTypes = {
  Military: "Military",
  A: "A",
  B: "B",
};

const SubmissionTypeDescriptions = {
  Military: "Military immunization",
  A: "A. Vaccination Dates:",
  B: "B. Immunity by Titer:",
};

const Results = {
  Neg: "Neg",
  Pos: "Pos",
};

export const ImmunizationVaricellaViewNew = forwardRef(({ data, onChange, disabled = false }, ref) => {
  const setValue = () => {
    if (onChange) onChange({ ...data });
  };

  useImperativeHandle(ref, () => ({
    validator: () => {
      const activeType = data.type;

      // Step 1: Remove unrelated top-level types
      Object.keys(data).forEach((key) => {
        if (key !== "type" && key !== activeType) {
          delete data[key];
        }
      });
    },
  }));

  const ensureTypePath = (type) => {
    if (!data[type]) data[type] = {};
  };

  return (
    <div className={classNames(styles.container, styles.lv0)}>
      {Object.keys(SubmissionTypes).map((type) => (
        <div key={type}>
          <DashboardRadio
            name="varicellaView-type"
            isSelected={data.type === type}
            onChange={(value) => setValue((data.type = value))}
            value={type}
            label={SubmissionTypeDescriptions[type]}
            containerClassName={styles.radioContainer}
            disabled={disabled}
          />
          {type === SubmissionTypes.A && data.type === SubmissionTypes.A && (
            <>
              <TypeAContent
                typeKey={type}
                field="date"
                label="Varicella by titer: "
                data={data}
                updateEntry={(field, key, value) => {
                  ensureTypePath(type);
                  if (!data.A[field]) data.A[field] = {};
                  data.A[field][key] = value;
                  setValue();
                }}
                disabled={disabled}
              />
            </>

          )}
          {type === SubmissionTypes.B && data.type === SubmissionTypes.B && (
            <>
              <TypeBContent
                typeKey={type}
                field="vaccinationDates"
                label=""
                data={data}
                updateEntry={(field, index, key, value) => {
                  ensureTypePath(type);
                  if (!data.B[field]) data.B[field] = [];
                  if (!data.B[field][index]) data.B[field][index] = {};
                  data.B[field][index][key] = value;
                  setValue();
                }}
                addEntry={(field) => {
                  ensureTypePath(type);
                  if (!data.B[field]) data.B[field] = [];
                  data.B[field].push({ date: null, result: null });
                  setValue();
                }}
                removeEntry={(field, index) => {
                  if (data.B?.[field]) {
                    data.B[field].splice(index, 1);
                    setValue();
                  }
                }}
                disabled={disabled}
              />
            </>
          )}
        </div>
      ))}
    </div>
  );
});

function TypeBContent({ field, label, data, typeKey, updateEntry, addEntry, removeEntry, disabled }) {
  const ALLOW_ADD_DATE = false;
  const dateList = data[typeKey]?.[field] || [];

  // Initialize first date if list is empty
  useEffect(() => {
    if (!disabled && dateList.length === 0) {
      addEntry(field);
    }
  }, [dateList, field, addEntry, disabled]);

  return (
    <div className={classNames(styles.testGroup)}>
      {dateList.map((entry, index) => (
        <div key={index} className={styles.testColumn}>
          <div key={index} className={styles.testRow}>
            <div className={classNames(styles.testItem)}>
              <LabeledDatePicker
                label={'Date. '} //{`${index + 1}. `}
                dateString={entry.date}
                onChange={(value) => updateEntry(field, index, "date", value)}
                disabled={disabled}
              />
            </div>
            <div className={classNames(styles.testItem, styles.lv2)}>
              <ResultRadioGroup
                name={`${field}-result-${index}`}
                value={entry.result}
                onChange={(val) => updateEntry(field, index, "result", val)}
                disabled={disabled}
              />
              {!disabled && ALLOW_ADD_DATE && (
                <button
                  type="button"
                  onClick={() => removeEntry(field, index)}
                  className={styles.removeButton}
                  aria-label="Remove"
                >
                  x
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
      {!disabled && ALLOW_ADD_DATE && (
        <AddButton onClick={() => addEntry(field)} label="Add Date" />
      )}
    </div>
  );
}


function TypeAContent({ typeKey, field, label, data, updateEntry, disabled }) {
  const value1 = data[typeKey]?.[field]?.date1 || null;
  const value2 = data[typeKey]?.[field]?.date2 || null;

  return (
    <div className={classNames(styles.testGroup)}>
      <div className={classNames(styles.testRow)}>
        <div className={classNames(styles.testItem)}>
          <LabeledDatePicker
            label={"1. "}
            dateString={value1}
            onChange={(value) => updateEntry(field, "date1", value)}
            disabled={disabled}
          />
        </div>
        <div className={classNames(styles.testItem, styles.lv2)}>
          <LabeledDatePicker
            label={"2. "}
            dateString={value2}
            onChange={(value) => updateEntry(field, "date2", value)}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}

function LabeledDatePicker({ label, dateString, onChange, disabled }) {
  return (
    <div className={styles.datePickerContainer}>
      <span>{label}</span>
      <FormDatePicker
        dateFormat={DATE_FORMAT_PATTERN}
        dateString={dateString}
        setDate={onChange}
        disabled={disabled}
      />
    </div>
  );
}

function ResultRadioGroup({ name, value, onChange, disabled }) {
  return (
    <div className={classNames(styles.resultContainer)}>
      <span>Result:</span>
      {Object.values(Results).map((result) => (
        <div key={result} className={classNames(styles.radios)}>
          <DashboardRadio
            name={name}
            isSelected={value === result}
            onChange={() => onChange(result)}
            value={result}
            label={result}
            containerClassName={styles.radioContainer}
            disabled={disabled}
          />
        </div>
      ))}
    </div>
  );
}

